import React, { useState, useEffect, useRef } from "react";
import {
  Flex,
  Heading,
  Button,
  Text,
  Spinner,
  Icon,
  IconButton,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { getPatientActivityLogAction } from "../../../../../actions/patientActions";
import { GET_PATIENT_ACTIVITY_LOG_RESET } from "../../../../../constants/patientConstants";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  backgroundLight,
  newBorder,
  primaryAlt,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import { FaArrowUp } from "react-icons/fa6";
import ActivityLogItem from "./ActivityLogItem";
import { TbAdjustmentsHorizontal } from "react-icons/tb";
import useThemeColors from "../../../../../hooks/useThemeColors";

const PatientActivityLog = ({ slug, id, patient }) => {
  const { t, i18n } = useTranslation();
  dayjs.locale(i18n.language);

  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // STATE
  const [page, setPage] = useState(1); // Keep track of current page
  const pageSize = 20; // Define how many logs to fetch per request

  const [showAll, setShowAll] = useState(false); // Keep track of whether to show all logs

  const logContainerRef = useRef(null);

  // REDUX
  const dispatch = useDispatch();

  const patientActivityLog = useSelector((state) => state.patientActivityLog);
  const { logs, loading } = patientActivityLog;

  // USE EFFECT
  useEffect(() => {
    dispatch({ type: GET_PATIENT_ACTIVITY_LOG_RESET }); // Reset the activity log state
    setPage(1); // Reset the page to 1
    dispatch(getPatientActivityLogAction(slug, id, 1, pageSize, showAll)); // Fetch either paginated or all logs
  }, [dispatch, id, showAll]); // Add 'showAll' as a dependency

  // HANDLERS
  const loadMoreLogs = () => {
    setShowAll(!showAll); // Set to true to load all logs
  };

  const scrollToTop = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTo({ top: 0, behavior: "smooth" }); // Scroll the container to the top
    }
  };

  const dynamicHeight = `${
    187 + (Math.min(patient?.treatmentPlans?.length || 0, 5) - 1) * 70
  }px`;

  // patient has no treatment plans
  const noTreatmentPlans = patient?.treatmentPlans?.length === 0;

  // LOGS
  // console.log("logs", logs);
  // console.log("patient", patient);

  // console.log("showAll", showAll);
  // console.log("noTreatmentPlans", noTreatmentPlans);

  return (
    <>
      <Flex
        direction="column"
        w="100%"
        p={["0rem 1.5rem 3rem", "0rem"]}
        position="relative"
      >
        <Flex align="baseline" justify="space-between" m="0 0 0rem" minH="36px">
          <Heading
            fontSize={["1rem", "1.125rem"]}
            color="primary"
            fontWeight="600"
            m={0}
            // p="0 0 0.75rem"
          >
            {t("patient.activityLog")}
          </Heading>
          <IconButton
            size="sm"
            fontSize="sm"
            // borderRadius="1rem"
            bg={backgroundLightMode}
            border={`0px solid ${newBorder}`}
            color={textSecondary}
            borderRadius="10rem"
            // p="1rem 1.5rem"
            // onClick={openSidePanelModalHandler}
            _hover={{ bg: primaryAlt, color: white }}
            isDisabled
          >
            <Icon as={TbAdjustmentsHorizontal} fontSize="0.875rem" />
          </IconButton>{" "}
        </Flex>

        {/* Display logs */}
        <Flex
          ref={logContainerRef}
          w="100%"
          bg={whiteMode}
          direction="column"
          m="0.5rem 0 0rem"
          p="0.75rem 0.75rem 0.75rem"
          borderRadius={["1.5rem", "1rem"]}
          border={`1px solid ${newBorderMode}`}
          position="relative"
          // minH="678px"
          // maxH="678px"
          //   minH="678px"
          // maxH="678px"
          // minH={showAll ? "678px" : "278px"}
          // maxH={showAll ? "678px" : "278px"}
          // h={showAll ? "600px" : dynamicHeight}
          h={noTreatmentPlans ? "462px" : showAll ? "600px" : dynamicHeight}
          overflowY={showAll ? "auto" : "hidden"}
          // h="100%"
        >
          {!showAll && logs.length > 2 && (
            <Flex
              w="100%"
              position="absolute"
              bottom="0"
              p="1rem 1.5rem"
              left="50%"
              transform="translateX(-50%)"
              m="0 auto"
              bg={`linear-gradient(180deg, rgba(255,255,255,0) 10%, ${whiteMode} 60%)`}
              zIndex={1}
            >
              <Button
                size="md"
                fontSize="0.8rem"
                m="0 auto"
                bg={backgroundLightMode}
                color={textSecondary}
                border={`0px solid ${newBorderMode}`}
                // boxShadow={`0px 4px 4px rgba(0, 0, 0, 0.03)`}
                p="1rem"
                borderRadius="1rem"
                w="80%"
                _hover={{ bg: primaryAlt, color: white }}
                onClick={loadMoreLogs}
                isDisabled={loading}
              >
                {t("common.showAll")}
              </Button>
            </Flex>
          )}

          <Flex direction="column" gap="1rem">
            {loading ? (
              <Flex
                w="100%"
                bg={whiteMode}
                borderRadius="1rem"
                p="1.5rem"
                align="center"
                justify="center"
              >
                <Spinner size="lg" />
              </Flex>
            ) : (
              logs.map((log, index) => (
                <ActivityLogItem
                  key={log._id || index}
                  index={index}
                  log={log}
                />
              ))
            )}
          </Flex>

          <Flex w="100%" m="0.5rem 0 0rem">
            <Flex
              align="center"
              justify="center"
              textAlign="center"
              w="100%"
              p="0.5rem 1rem"
              bg={whiteMode}
              borderRadius="1rem"
            >
              <Text fontSize="xs" color={textSecondary} fontWeight="500">
                {t("common.noMoreLogs")}
              </Text>
            </Flex>
          </Flex>

          {showAll && (
            <>
              <Flex align="center" m="0.5rem auto 1rem">
                <Button
                  size="sm"
                  fontSize="xs"
                  fontWeight="500"
                  bg={backgroundLight}
                  p="0.5rem 1rem"
                  borderRadius="1rem"
                  color={textSecondary}
                  textAlign="center"
                  onClick={loadMoreLogs}
                  mr="0.5rem"
                  _hover={{
                    bg: primaryAlt,
                    color: white,
                  }}
                >
                  {t("common.showLess")}
                </Button>
                <IconButton
                  size="sm"
                  bg={backgroundLight}
                  color={textSecondary}
                  borderRadius="10rem"
                  onClick={scrollToTop}
                  _hover={{ bg: primaryAlt, color: white }}
                >
                  <Icon as={FaArrowUp} fontSize="0.75rem" />
                </IconButton>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default PatientActivityLog;
